import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useGetProformaInvoiceListQuery, useLazyGetProformaInvoiceDetailQuery } from '../../api/endpoints/proformaInvoice';
import ProformaInvoicePdfForm from './utils/ProformaInvoicePdfForm';

const ProformaInvoicePage = () => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 20, page: 1 });
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);

    const { data: resp, isFetching } = useGetProformaInvoiceListQuery({
        PageNumber: lazyParams.page,
        PageSize: lazyParams.rows
    });
    const [triggerGetDetail] = useLazyGetProformaInvoiceDetailQuery();

    const rows = resp?.data?.rows ?? [];
    const totalRecords = resp?.data?.totalCount ?? 0;

    const onPage = useCallback((event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: Math.floor(event.first / event.rows) + 1
        });
    }, []);

    const handleDownloadPdf = useCallback(async (row) => {
        if (!row?.id) return;
        setDownloadingPdfId(row.id);
        try {
            const detail = await triggerGetDetail({ proformaId: row.id }).unwrap();
            if (!detail?.data) return;
            const pdfTranslations = {
                pdfTitle: th('proformaInvoice.pdfTitle'),
                pdfId: th('proformaInvoice.pdfId'),
                createdDate: th('proformaInvoice.createdDate'),
                printDate: th('proformaInvoice.printDate'),
                customerNameSurname: th('proformaInvoice.customerNameSurname'),
                phone: th('proformaInvoice.phone'),
                chassisNo: th('proformaInvoice.chassisNo'),
                licensePlate: th('proformaInvoice.licensePlate'),
                km: th('proformaInvoice.km'),
                partTab: th('proformaInvoice.partTab'),
                laborTab: th('proformaInvoice.laborTab'),
                otherCostTab: th('proformaInvoice.otherCostTab'),
                itemCode: th('proformaInvoice.itemCode'),
                itemName: th('proformaInvoice.itemName'),
                description: th('proformaInvoice.description'),
                quantity: th('proformaInvoice.quantity'),
                unitPrice: th('proformaInvoice.unitPrice'),
                totalPrice: th('proformaInvoice.totalPrice'),
                warrantyType: th('proformaInvoice.warrantyType'),
                customerPays: th('proformaInvoice.customerPays'),
                karsanWarranty: th('proformaInvoice.karsanWarranty'),
                itemsSubtotal: th('proformaInvoice.itemsSubtotal'),
                discountPrice: th('proformaInvoice.discountPrice'),
                netPrice: th('proformaInvoice.netPrice')
            };
            const pdfForm = new ProformaInvoicePdfForm();
            const pdfDoc = pdfForm.generate(detail.data, pdfTranslations);
            pdfDoc.download(`ProformaFatura_${detail.data.id}.pdf`);
        } catch {
            // baseQueryWithReauth global hata gösteriyor
        } finally {
            setDownloadingPdfId(null);
        }
    }, [triggerGetDetail, th]);

    const actionTemplate = (row) => (
        <div className="flex gap-1 justify-content-center">
            <Button
                icon="pi pi-pencil"
                rounded
                text
                severity="info"
                size="small"
                tooltip={th('common.edit')}
                tooltipOptions={{ position: 'top' }}
                onClick={() => navigate(`/proforma-invoice/${row.id}`)}
            />
            <Button
                icon="pi pi-file-pdf"
                rounded
                text
                severity="danger"
                size="small"
                tooltip={th('common.downloadPdf')}
                tooltipOptions={{ position: 'top' }}
                loading={downloadingPdfId === row.id}
                onClick={() => handleDownloadPdf(row)}
            />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('proformaInvoice.listTitle')}</h1>
                <Button
                    label={th('proformaInvoice.addNew')}
                    icon="pi pi-plus"
                    severity="success"
                    onClick={() => navigate('/proforma-invoice/new')}
                />
            </div>

            <DataTable
                value={rows}
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                totalRecords={totalRecords}
                onPage={onPage}
                className="p-datatable-gridlines"
                showGridlines
                dataKey="id"
                loading={isFetching}
                emptyMessage={th('common.noRecords')}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
            >
                <Column body={actionTemplate} style={{ width: '90px' }} align="center" />
                <Column field="customerNameSurname" header={th('proformaInvoice.customerNameSurname')} />
                <Column field="netPrice" header={th('proformaInvoice.netPrice')} body={(row) => row.netPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} />
                <Column field="createdDate" header={th('proformaInvoice.createdDate')} body={(row) => row.createdDate ? new Date(row.createdDate).toLocaleDateString('tr-TR') : ''} />
            </DataTable>
        </div>
    );
};

export default ProformaInvoicePage;
